/**
 * Client-side errors that are safe to drop from diagnostics and error tracking.
 *
 * Two classes live here:
 *   1. Benign browser notifications that arrive on `window.onerror` but are not
 *      real exceptions (the ResizeObserver loop notice).
 *   2. Recoverable React hydration mismatches (#418/#423/#425) — self-correcting
 *      and, in production, overwhelmingly caused by browser extensions mutating
 *      the server HTML before hydration.
 *
 * Kept in its own dependency-free module because both the pre-hydration error
 * buffer (`client-instrumentation-buffer.ts`, part of the earliest client
 * bootstrap) and the hydrated diagnostic system need the same predicates.
 */

/**
 * The ResizeObserver entries below are mandated by the specification: when an
 * observer callback resizes an observed element, the browser finishes the frame
 * and reports the remaining notifications through the global error handler
 * before delivering them on the next frame. Nothing is lost and nothing
 * crashed, so treating them as uncaught exceptions produces user-facing
 * "reload the page" toasts and error-tracking noise for a self-correcting
 * layout event.
 *
 * They can also originate from third-party observers we do not control
 * (floating-ui/radix popper, the Remotion player, virtualizers, dnd-kit), so
 * they must be filtered at the global handler rather than only at each
 * in-app observer call site.
 *
 * @see https://drafts.csswg.org/resize-observer/#deliver-resize-loop-error
 */
const BENIGN_BROWSER_NOTIFICATIONS = [
  "resizeobserver loop completed with undelivered notifications",
  "resizeobserver loop limit exceeded",
] as const;

/**
 * Whether a global error message is a benign browser notification rather than a
 * real uncaught exception. Callers should skip diagnostics and error reporting
 * for these.
 *
 * Accepts `unknown` on purpose: `ErrorEvent.error` carries whatever was thrown,
 * which is not required to be an `Error` (`throw { message: 42 }` is valid JS
 * and third-party code does it). This runs inside global error handlers, where
 * throwing would drop the very report it is filtering.
 */
export function isBenignBrowserNotification(message: unknown): boolean {
  if (typeof message !== "string" || !message) return false;
  const normalized = message.toLowerCase();
  return BENIGN_BROWSER_NOTIFICATIONS.some((pattern) =>
    normalized.includes(pattern),
  );
}

/**
 * React "recoverable" hydration mismatch errors (#418 element mismatch, #423
 * hydration recovery, #425 text mismatch).
 *
 * These are RECOVERABLE by design: when the server-rendered DOM does not match
 * what the client would render, React discards the mismatched subtree and
 * re-renders it on the client. The UI ends up correct — nothing crashed and
 * the user sees no breakage — but React still reports the mismatch through
 * `reportError()`, which surfaces on `window.onerror` and is picked up by
 * PostHog's exception autocapture as a handled `$exception`.
 *
 * In production these are overwhelmingly triggered by browser extensions
 * mutating the server HTML in the pre-hydration window — password managers
 * (1Password, LastPass, Dashlane, Bitwarden) injecting icons/attributes into
 * login and signup fields, Grammarly and Dark Reader decorating <body>/<html>,
 * page translators rewriting text nodes — none of which the application can
 * prevent. Treating them as actionable exceptions produces user-facing
 * "reload the page" toasts and error-tracking noise for a self-correcting
 * event, exactly like the ResizeObserver loop notice above.
 *
 * Genuine APP-CODE hydration bugs are caught earlier and louder, so silencing
 * the production report does not blind us to regressions:
 *   - development shows the full React hydration error + component stack in the
 *     console and the Next.js error overlay (and analytics is disabled in dev),
 *   - CI runs dedicated hydration regression tests for the auth wrappers and
 *     the /home client tree.
 *
 * Matching covers both the minified production string (which carries the
 * numeric code and the react.dev/errors URL) and the readable development
 * messages.
 */
const RECOVERABLE_HYDRATION_ERROR_PATTERNS = [
  // Minified production: "Minified React error #418; visit
  // https://react.dev/errors/418?args[]=HTML&args[]= ..."
  "react error #418",
  "react error #423",
  "react error #425",
  "react.dev/errors/418",
  "react.dev/errors/423",
  "react.dev/errors/425",
  // Development (unminified) messages:
  "hydration failed because",
  "there was an error while hydrating",
  "hydration completed but contains mismatches",
  "text content does not match server-rendered html",
  "text content did not match",
] as const;

/**
 * Whether a global error message is a recoverable React hydration mismatch.
 * See `RECOVERABLE_HYDRATION_ERROR_PATTERNS` for the full rationale.
 *
 * Accepts `unknown` for the same reason as `isBenignBrowserNotification`: it
 * runs inside global error handlers where the thrown value may not be a string.
 */
export function isRecoverableHydrationError(message: unknown): boolean {
  if (typeof message !== "string" || !message) return false;
  const normalized = message.toLowerCase();
  return RECOVERABLE_HYDRATION_ERROR_PATTERNS.some((pattern) =>
    normalized.includes(pattern),
  );
}

/**
 * Whether a client-side error should be dropped from diagnostics AND error
 * tracking because it is self-correcting and (in practice) not caused by
 * application code: benign browser notifications (ResizeObserver) or recoverable
 * React hydration mismatches (usually browser-extension DOM mutation).
 *
 * This is the single predicate every global `window.onerror`-style handler
 * should consult so the two classes are filtered identically at every boundary.
 */
export function isIgnorableClientError(message: unknown): boolean {
  return (
    isBenignBrowserNotification(message) || isRecoverableHydrationError(message)
  );
}

/**
 * Whether a PostHog `$exception` event describes nothing but benign browser
 * notifications.
 *
 * PostHog's `capture_exceptions` autocapture installs its own `window.onerror`
 * and `unhandledrejection` listeners, so filtering inside the application's
 * handlers does not keep these notices out of error tracking — they have to be
 * dropped at the analytics boundary too.
 *
 * Takes the raw event properties rather than a posthog-js type so this module
 * stays dependency-free for the pre-hydration bootstrap path.
 */
export function hasOnlyBenignExceptions(properties: unknown): boolean {
  if (!properties || typeof properties !== "object") return false;

  const record = properties as Record<string, unknown>;
  const exceptions = record.$exception_list;

  // A chained exception must only be dropped when every link is benign,
  // otherwise a real error sharing the report would be lost with it.
  if (Array.isArray(exceptions) && exceptions.length > 0) {
    return exceptions.every((exception) =>
      isBenignBrowserNotification(
        (exception as Record<string, unknown> | null | undefined)?.value,
      ),
    );
  }

  return isBenignBrowserNotification(record.$exception_message);
}

/**
 * Whether a PostHog `$exception` event describes nothing but errors that are
 * safe to drop: benign browser notifications OR recoverable React hydration
 * mismatches. This is the event-shaped counterpart to `isIgnorableClientError`,
 * used at the PostHog `before_send` boundary (PostHog's autocapture installs
 * its own `window.onerror`, so the application-side handlers cannot keep these
 * out of error tracking on their own).
 */
export function hasOnlyIgnorableExceptions(properties: unknown): boolean {
  if (!properties || typeof properties !== "object") return false;

  const record = properties as Record<string, unknown>;
  const exceptions = record.$exception_list;

  // A chained exception must only be dropped when EVERY link is ignorable,
  // otherwise a real error sharing the report would be lost with it.
  if (Array.isArray(exceptions) && exceptions.length > 0) {
    return exceptions.every((exception) =>
      isIgnorableClientError(
        (exception as Record<string, unknown> | null | undefined)?.value,
      ),
    );
  }

  return isIgnorableClientError(record.$exception_message);
}
